import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import {
    CancelWorkflowExecutionRequest,
    CancelWorkflowExecutionActionRequest,
    CreateWorkflowRequest,
    DeleteWorkflowRequest,
    UpdateWorkflowRequest,
    ListWorkflowsRequest,
    ListWorkflowExecutionActionsRequest,
    ListWorkflowExecutionOutputsRequest,
    type ListWorkflowExecutionOutputsResponse_ActionOutput,
    StartWorkflowRequest,
    WorkflowAction,
    WorkflowAction_Limits,
    WorkflowExecution,
    WorkflowExecutionAction,
    WorkflowExecutionActionPhase,
    WorkflowStep,
    WorkflowTrigger,
    WorkflowTrigger_Manual,
    WorkflowTriggerContext,
    WorkflowTriggerContext_Projects,
    type Workflow,
    WorkflowExecutionPhase,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { useOrganization } from "@/queries/organization-queries";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";

export type PlainWorkflow = PlainMessage<Workflow>;
export type PlainWorkflowTrigger = PlainMessage<WorkflowTrigger>;
export type PlainWorkflowAction = PlainMessage<WorkflowAction>;
export type PlainWorkflowStep = PlainMessage<WorkflowStep>;
export type PlainWorkflowExecution = PlainMessage<WorkflowExecution>;
export type PlainWorkflowExecutionAction = PlainMessage<WorkflowExecutionAction>;

export function toPlainWorkflow(workflow: Workflow): PlainWorkflow {
    return toPlainMessage(workflow);
}

export const workflowQueryKey = {
    list: () => keyWithPrincipal(["automations", "list"]),
    get: (workflowId?: string) => keyWithPrincipal(["automations", { workflowId }]),
    webhookSecret: (workflowId?: string) => keyWithPrincipal(["automations", "webhook-secret", { workflowId }]),
    executionsOfWorkflow: (workflowId: string, filter?: WorkflowExecutionsFilter) =>
        keyWithPrincipal(["workflow-executions", "from-workflow", workflowId, filter ?? {}]),
    execution: (workflowExecutionId?: string) => keyWithPrincipal(["workflow-executions", { workflowExecutionId }]),
    executionActions: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-actions", "list", { workflowExecutionId }]),
    executionAction: (workflowExecutionActionId?: string) =>
        keyWithPrincipal(["workflow-execution-actions", { workflowExecutionActionId }]),
    executionOutputs: (workflowExecutionId: string) =>
        keyWithPrincipal(["workflow-execution-outputs", { workflowExecutionId }]),
};

export const handleWorkflowEvents = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.resourceType) {
        case ResourceType.WORKFLOW: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const workflow = await refetchWorkflow(api, evt.resourceId);
                setWorkflowInCache(client, evt.resourceId, workflow ? toPlainWorkflow(workflow) : undefined);
            }
            if (evt.operation === ResourceOperation.CREATE) {
                await client.invalidateQueries({ queryKey: workflowQueryKey.list() });
            }
            if (evt.operation === ResourceOperation.DELETE) {
                setWorkflowInCache(client, evt.resourceId, undefined);
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const execution = await refetchWorkflowExecution(api, evt.resourceId);
                if (execution?.metadata?.workflowId) {
                    const plainExecution = toPlainMessage(execution);
                    setWorkflowExecutionInCache(client, {
                        workflowId: execution.metadata.workflowId,
                        workflowExecutionId: evt.resourceId,
                        workflowExecution: plainExecution,
                    });
                }
            }
            if (evt.operation === ResourceOperation.CREATE) {
                const execution = await refetchWorkflowExecution(api, evt.resourceId);
                if (execution?.metadata?.workflowId) {
                    const plainExecution = toPlainMessage(execution);
                    setWorkflowExecutionInCache(client, {
                        workflowId: execution.metadata.workflowId,
                        workflowExecutionId: evt.resourceId,
                        workflowExecution: plainExecution,
                    });
                }
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedExecution = client.getQueryData<PlainWorkflowExecution>(
                    workflowQueryKey.execution(evt.resourceId),
                );
                if (cachedExecution?.metadata?.workflowId) {
                    setWorkflowExecutionInCache(client, {
                        workflowId: cachedExecution.metadata.workflowId,
                        workflowExecutionId: evt.resourceId,
                        workflowExecution: undefined,
                    });
                }
            }
            break;
        }
        case ResourceType.WORKFLOW_EXECUTION_ACTION: {
            if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: action.metadata.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: action ? toPlainMessage(action) : undefined,
                    });
                }
            }
            if (evt.operation === ResourceOperation.CREATE) {
                const action = await refetchWorkflowExecutionAction(api, evt.resourceId);
                if (action?.metadata?.workflowExecutionId) {
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(action?.metadata?.workflowExecutionId),
                    });
                }
            }
            if (evt.operation === ResourceOperation.DELETE) {
                const cachedAction = client.getQueryData<PlainWorkflowExecutionAction>(
                    workflowQueryKey.executionAction(evt.resourceId),
                );
                if (cachedAction?.metadata?.workflowExecutionId) {
                    setWorkflowExecutionActionInCache(client, {
                        workflowExecutionId: cachedAction?.metadata?.workflowExecutionId,
                        workflowExecutionActionId: evt.resourceId,
                        action: undefined,
                    });
                    await client.invalidateQueries({
                        queryKey: workflowQueryKey.executionActions(cachedAction?.metadata?.workflowExecutionId),
                    });
                }
            }
            break;
        }
    }
};

const refetchWorkflow = async (api: GitpodAPI, workflowId: string) => {
    try {
        const response = await api.workflowService.getWorkflow({ workflowId });
        return response.workflow;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow", workflowId);
        }
    }
};

const refetchWorkflowExecution = async (api: GitpodAPI, workflowExecutionId: string) => {
    try {
        const response = await api.workflowService.getWorkflowExecution({ workflowExecutionId });
        return response.workflowExecution;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution", workflowExecutionId);
        }
    }
};

const refetchWorkflowExecutionAction = async (api: GitpodAPI, workflowExecutionActionId: string) => {
    try {
        const response = await api.workflowService.getWorkflowExecutionAction({ workflowExecutionActionId });
        return response.workflowExecutionAction;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch workflow execution action", workflowExecutionActionId);
        }
    }
};

export const useListWorkflows = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.list(),
        queryFn: async (): Promise<PlainWorkflow[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWorkflowsRequest();
            const workflows = await autoPaginate(
                (params) => api.workflowService.listWorkflows(params),
                request,
                (response) => response.workflows,
            );
            return workflows.map(toPlainWorkflow);
        },
        throwOnError: false, // Handle errors locally, don't throw to error boundary
        retry: defaultRetry,
        enabled: !!user,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetWorkflow = (workflowId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: workflowQueryKey.get(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const { workflow } = await api.workflowService.getWorkflow({
                workflowId,
            });

            if (!workflow) {
                throw new Error("Error fetching workflow");
            }

            return toPlainWorkflow(workflow);
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type WorkflowExecutionsFilter = {
    statusPhases?: WorkflowExecutionPhase[];
    hasFailedActions?: boolean;
};

export type UseListWorkflowExecutionsParams = {
    enabled: boolean;
    workflowId: string;
    filter?: WorkflowExecutionsFilter;
};

export const useListWorkflowExecutions = ({ enabled, workflowId, filter }: UseListWorkflowExecutionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const query = useInfiniteQuery({
        queryKey: workflowQueryKey.executionsOfWorkflow(workflowId, filter),
        queryFn: async ({ pageParam }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.workflowService.listWorkflowExecutions({
                filter: {
                    workflowIds: [workflowId],
                    statusPhases: filter?.statusPhases,
                    hasFailedActions: filter?.hasFailedActions,
                },
                pagination: new PaginationRequest({ token: pageParam }),
            });

            return {
                executions: response.workflowExecutions.map((execution) => toPlainMessage(execution)),
                nextToken: response.pagination?.nextToken || null,
            };
        },
        getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
        initialPageParam: "",
        enabled: enabled && !!user,
        throwOnError: false, // Handle errors locally, don't throw to error boundary
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });

    // Flatten pages into a single array for backward compatibility
    const executions = query.data?.pages.flatMap((page) => page.executions) ?? [];

    return {
        data: executions,
        error: query.error,
        isLoading: query.isLoading,
        isPending: query.isPending,
        isError: query.isError,
        refetch: query.refetch,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
    };
};

export type UseListWorkflowExecutionActionsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionActions = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionActionsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.executionActions(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionAction[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWorkflowExecutionActionsRequest({
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const workflowExecutionActions = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionActions(params),
                request,
                (response) => response.workflowExecutionActions,
            );
            return workflowExecutionActions.map((action) => toPlainMessage(action));
        },
        enabled: enabled && !!user,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetWorkflowExecution = (workflowExecutionId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainWorkflowExecution, Error>({
        queryKey: workflowQueryKey.execution(workflowExecutionId),
        queryFn: async () => {
            if (!workflowExecutionId) {
                throw new Error("No workflowExecutionId provided");
            }

            const { workflowExecution } = await api.workflowService.getWorkflowExecution({
                workflowExecutionId,
            });

            if (!workflowExecution) {
                throw new Error("Error fetching workflow execution");
            }

            return toPlainMessage(workflowExecution);
        },
        enabled: !!workflowExecutionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetWorkflowExecutionAction = (workflowExecutionActionId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainWorkflowExecutionAction, Error>({
        queryKey: workflowQueryKey.executionAction(workflowExecutionActionId),
        queryFn: async () => {
            if (!workflowExecutionActionId) {
                throw new Error("No workflowExecutionActionId provided");
            }

            const { workflowExecutionAction } = await api.workflowService.getWorkflowExecutionAction({
                workflowExecutionActionId,
            });

            if (!workflowExecutionAction) {
                throw new Error("Error fetching workflow execution action");
            }

            return toPlainMessage(workflowExecutionAction);
        },
        enabled: !!workflowExecutionActionId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type CreateWorkflowParams = {
    name?: string;
    description?: string;
    triggers?: WorkflowTrigger[];
    steps?: WorkflowStep[];
    limits?: {
        maxParallel?: number;
        maxTotal?: number;
    };
};

export const useCreateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWorkflowParams) => {
            const defaultTriggers = [
                new WorkflowTrigger({
                    trigger: {
                        case: "manual",
                        value: new WorkflowTrigger_Manual(),
                    },
                    context: new WorkflowTriggerContext({
                        context: {
                            case: "projects",
                            value: new WorkflowTriggerContext_Projects({
                                projectIds: [],
                            }),
                        },
                    }),
                }),
            ];

            const defaultSteps = [
                new WorkflowStep({
                    step: {
                        case: "agent",
                        value: {
                            prompt: "With prompts you can send messages to Ona Agent",
                        },
                    },
                }),
                new WorkflowStep({
                    step: {
                        case: "task",
                        value: {
                            command: "echo 'with commands you can run shell commands'",
                        },
                    },
                }),
            ];

            const request = new CreateWorkflowRequest({
                name: params.name,
                description: params.description || "",
                triggers: params.triggers || defaultTriggers,
                action: new WorkflowAction({
                    limits: new WorkflowAction_Limits({
                        maxParallel: params.limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: params.limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: params.steps || defaultSteps,
                }),
            });
            const response = await api.workflowService.createWorkflow(request);
            if (!response.workflow) {
                throw new Error("Error creating workflow");
            }
            return response.workflow;
        },
        onSuccess: (workflow) => {
            if (workflow) {
                const plainWorkflow = toPlainWorkflow(workflow);
                setWorkflowInCache(queryClient, workflow.id, plainWorkflow);
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateWorkflowParams = {
    workflowId: string;
    name?: string;
    description?: string;
    executor?: PlainMessage<Subject>;
    action?: PlainWorkflowAction;
    triggers?: PlainWorkflowTrigger[];
};

export const useUpdateWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, name, description, executor, action, triggers }: UpdateWorkflowParams) => {
            const request = new UpdateWorkflowRequest({
                workflowId,
                name,
                description,
                executor,
                action,
                triggers,
            });
            const response = await api.workflowService.updateWorkflow(request);
            if (!response.workflow) {
                throw new Error("Failed to update workflow");
            }
            return toPlainWorkflow(response.workflow);
        },
        onSuccess: (updatedWorkflow) => {
            setWorkflowInCache(queryClient, updatedWorkflow.id, updatedWorkflow);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, force = false }: { workflowId: string; force?: boolean }) => {
            const request = new DeleteWorkflowRequest({ workflowId, force });
            return await api.workflowService.deleteWorkflow(request);
        },
        onSuccess: (_, { workflowId }) => {
            setWorkflowInCache(queryClient, workflowId, undefined);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type StartWorkflowParams = {
    workflowId: string;
    contextOverride?: PlainMessage<WorkflowTriggerContext>;
};

export const useStartWorkflow = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ workflowId, contextOverride }: StartWorkflowParams) => {
            const request = new StartWorkflowRequest({
                workflowId,
                contextOverride: contextOverride ? new WorkflowTriggerContext(contextOverride) : undefined,
            });
            const response = await api.workflowService.startWorkflow(request);
            if (!response.workflowExecution) {
                throw new Error("Failed to start workflow");
            }
            return toPlainMessage(response.workflowExecution);
        },
        onSuccess: (execution, { workflowId }) => {
            setWorkflowExecutionInCache(queryClient, {
                workflowId: workflowId,
                workflowExecutionId: execution.id,
                workflowExecution: execution,
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (execution: PlainWorkflowExecution) => {
            const request = new CancelWorkflowExecutionRequest({ workflowExecutionId: execution.id });
            await api.workflowService.cancelWorkflowExecution(request);
            return execution.id;
        },
        onSuccess: (_executionId: string, execution: PlainWorkflowExecution) => {
            if (execution.metadata?.workflowId) {
                setWorkflowExecutionInCache(queryClient, {
                    workflowId: execution.metadata.workflowId,
                    workflowExecutionId: execution.id,
                    workflowExecution: new WorkflowExecution({
                        ...execution,
                        status: { ...execution.status, phase: WorkflowExecutionPhase.STOPPING },
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCancelWorkflowExecutionAction = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (action: PlainWorkflowExecutionAction) => {
            const request = new CancelWorkflowExecutionActionRequest({
                workflowExecutionActionId: action.id,
            });
            await api.workflowService.cancelWorkflowExecutionAction(request);
            return action.id;
        },
        onSuccess: (_actionId: string, action: PlainWorkflowExecutionAction) => {
            if (action.metadata?.workflowExecutionId) {
                setWorkflowExecutionActionInCache(queryClient, {
                    workflowExecutionId: action.metadata.workflowExecutionId,
                    workflowExecutionActionId: action.id,
                    action: new WorkflowExecutionAction({
                        ...action,
                        status: {
                            ...action.status,
                            phase: WorkflowExecutionActionPhase.STOPPING,
                        },
                    }),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export function setWorkflowInCache(client: QueryClient, workflowId: string, workflow?: PlainWorkflow) {
    client.setQueryData(workflowQueryKey.get(workflowId), workflow ? workflow : null);
    client.setQueryData(workflowQueryKey.list(), (currentData?: PlainWorkflow[]) => {
        if (!currentData) {
            return workflow ? [workflow] : currentData;
        }

        const updatedList = currentData
            .map((w: PlainWorkflow) => {
                if (w.id !== workflowId) {
                    return w;
                }
                return workflow;
            })
            .filter((w) => !!w);

        // Add to the top of the list if it doesn't already exist
        if (workflow && !currentData.some((w) => w.id === workflowId)) {
            updatedList.unshift(workflow);
        }

        return updatedList;
    });
}

export function setWorkflowExecutionInCache(
    client: QueryClient,
    data: {
        workflowId: string;
        workflowExecutionId: string;
        workflowExecution?: PlainWorkflowExecution;
    },
) {
    const { workflowId, workflowExecutionId, workflowExecution } = data;

    client.setQueryData(workflowQueryKey.execution(workflowExecutionId), workflowExecution ? workflowExecution : null);
    client.setQueryData(workflowQueryKey.executionsOfWorkflow(workflowId), (currentData?: PlainWorkflowExecution[]) => {
        if (!currentData) {
            return workflowExecution ? [workflowExecution] : currentData;
        }

        const updatedList = currentData
            .map((e: PlainWorkflowExecution) => {
                if (e.id !== workflowExecutionId) {
                    return e;
                }
                return workflowExecution;
            })
            .filter((e) => !!e);

        // Add to the top of the list if it doesn't already exist
        if (workflowExecution && !currentData.some((e) => e.id === workflowExecutionId)) {
            updatedList.unshift(workflowExecution);
        }

        return updatedList;
    });
}

export function setWorkflowExecutionActionInCache(
    client: QueryClient,
    data: {
        workflowExecutionId: string;
        workflowExecutionActionId: string;
        action?: PlainWorkflowExecutionAction;
    },
) {
    const { workflowExecutionId, workflowExecutionActionId, action } = data;
    client.setQueryData(workflowQueryKey.executionAction(workflowExecutionActionId), action ? action : null);
    client.setQueryData(
        workflowQueryKey.executionActions(workflowExecutionId),
        (currentData?: PlainWorkflowExecutionAction[]) => {
            if (!currentData) {
                return action ? [action] : currentData;
            }

            const updatedList = currentData
                .map((a: PlainWorkflowExecutionAction) => {
                    if (a.id !== workflowExecutionActionId) {
                        return a;
                    }
                    return action;
                })
                .filter((a) => !!a);

            // Add to the top of the list if it doesn't already exist
            if (action && !currentData.some((a) => a.id === workflowExecutionActionId)) {
                updatedList.unshift(action);
            }

            return updatedList;
        },
    );
}

export const useGetWorkflowWebhookSecret = (workflowId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: workflowQueryKey.webhookSecret(workflowId),
        queryFn: async () => {
            if (!workflowId) {
                throw new Error("No workflowId provided");
            }

            const response = await api.workflowService.getWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Error fetching webhook secret");
            }

            return response.secret;
        },
        enabled: !!workflowId,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useRotateWorkflowWebhookSecret = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (workflowId: string) => {
            const response = await api.workflowService.rotateWorkflowWebhookSecret({
                workflowId,
            });

            if (!response.secret) {
                throw new Error("Failed to rotate webhook secret");
            }

            return response.secret;
        },
        onSuccess: (newSecret, workflowId) => {
            queryClient.setQueryData(workflowQueryKey.webhookSecret(workflowId), newSecret);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainWorkflowExecutionActionOutput = PlainMessage<ListWorkflowExecutionOutputsResponse_ActionOutput>;

export type UseListWorkflowExecutionOutputsParams = {
    enabled: boolean;
    workflowExecutionId: string;
};

export const useListWorkflowExecutionOutputs = ({
    enabled,
    workflowExecutionId,
}: UseListWorkflowExecutionOutputsParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: workflowQueryKey.executionOutputs(workflowExecutionId),
        queryFn: async (): Promise<PlainWorkflowExecutionActionOutput[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWorkflowExecutionOutputsRequest({
                filter: { workflowExecutionIds: [workflowExecutionId] },
            });
            const outputs = await autoPaginate(
                (params) => api.workflowService.listWorkflowExecutionOutputs(params),
                request,
                (response) => response.outputs,
            );
            return outputs.map((output) => toPlainMessage(output));
        },
        enabled: enabled && !!user,
        throwOnError: false,
        retry: defaultRetry,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useCanCreateAutomation = () => {
    const { data: organization } = useOrganization();
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canCreateAutomations =
        organization && hasPermission(ResourceType.ORGANIZATION, organization.id, "workflow:create");
    return {
        canCreateAutomations,
        isLoading: isLoadingPermissions,
    };
};

export const useCanEditAutomation = (workflowId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canEditAutomation = workflowId ? hasPermission(ResourceType.WORKFLOW, workflowId, "workflow:update") : false;
    return {
        canEditAutomation,
        isLoading: isLoadingPermissions,
    };
};

export const useCanDeleteAutomation = (workflowId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canDeleteAutomation = workflowId
        ? hasPermission(ResourceType.WORKFLOW, workflowId, "workflow:delete")
        : false;
    return {
        canDeleteAutomation,
        isLoading: isLoadingPermissions,
    };
};

export const useCanShareAutomation = (workflowId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canShareAutomation = workflowId ? hasPermission(ResourceType.WORKFLOW, workflowId, "workflow:grant") : false;
    return {
        canShareAutomation,
        isLoading: isLoadingPermissions,
    };
};

export const useCanRunAutomation = (workflowId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canRunAutomation = workflowId
        ? hasPermission(ResourceType.WORKFLOW, workflowId, "workflowexecution:create")
        : false;
    return {
        canRunAutomation,
        isLoading: isLoadingPermissions,
    };
};
